export interface AuthUser {
  id: string;
  email: string;
  emailVerified: boolean;
}

export interface AuthSession {
  user: AuthUser;
  token: string;
  // Role slugs from the ID token's custom claims (PRD §6.4). Lives on
  // the session rather than the user because it is a property of the
  // *token*: it changes when a new token is minted, and the session
  // listener is what delivers that change. A token with no roles claim, or
  // for an account with none, carries `[]` — never treat an absent claim as
  // privilege.
  //
  // Presentation only (§6.1 principle 4). The permission set is resolved
  // server-side per request and is deliberately not in the claims.
  roles: readonly string[];
}

export class AuthError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

export interface AuthClient {
  register(email: string, password: string): Promise<AuthSession>;
  login(email: string, password: string): Promise<AuthSession>;
  signOut(): Promise<void>;
  // Firebase's implementation rejects only on an outage — non-disclosure
  // of whether the address is registered is the server's property, not
  // this method's. See FirebaseAuthClient.requestPasswordReset.
  requestPasswordReset(email: string): Promise<void>;
  getCurrentSession(): AuthSession | null;

  // Optional: subscribe to session changes. Firebase clients use this
  // to push token-refresh updates into the Pinia store; the mock
  // doesn't need it (its tokens don't expire). Returns an unsubscribe
  // function. Callers should fire once with the current state so
  // subscribers don't miss the initial value.
  onSessionChanged?(cb: (session: AuthSession | null) => void): () => void;

  // Optional: resolves once the client has determined its initial
  // auth state. Firebase restores sessions from IndexedDB async, so
  // main.ts awaits this before app.mount() to avoid flashing an
  // unauthenticated UI to returning users. Mock resolves immediately.
  ready?(): Promise<void>;

  // Optional: re-send an email verification link to the signed-in
  // user. The "verify your email to purchase" UI uses this so the
  // user can retry without having to dig through old inbox messages.
  // Throws `AuthError('invalid-credentials', ...)` when signed out, and
  // otherwise rejects with the `ApiError` from `POST
  // /me/verification-email` — including the rate limit, which is ours
  // because the send goes through our API rather than the Firebase SDK. Mock
  // doesn't need this (its users are always emailVerified=true).
  resendVerificationEmail?(): Promise<void>;

  // Optional: force-refresh the signed-in user's profile AND ID token,
  // returning the reconciled session (null if signed out).
  //
  // Firebase's getIdToken() returns a CACHED token whose claims — most
  // importantly email_verified — can lag the live user state right
  // after the user clicks the verification link. The UI gates on the
  // live `user.emailVerified` (so it shows the Subscribe CTA), while the
  // API verifies the token's stale `email_verified: false` claim and
  // returns 403 `email_not_verified`. This reloads the user profile and
  // force-mints a fresh token so the two agree. Mock returns its current
  // session unchanged (its users are always emailVerified=true).
  refreshSession?(): Promise<AuthSession | null>;
}
